import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { loginUser, clearError, setAuthState } from '../../store/slices/authSlice';
import { graphqlRequest } from '../../service/graphql-client';
import { GQ_LOGIN_WITH_GOOGLE, GQ_ME_QUERY } from '../../service/graphql-mutations';
import { GoogleLogin } from '@react-oauth/google';

export default function LoginModal({ onSuccess, onShowSignup, onShowForgotPassword }) {
  const dispatch = useAppDispatch();
  const { isLoading, error, accessToken } = useAppSelector((state) => state.auth);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    rememberMe: false
  });
  const [isCheckingToken, setIsCheckingToken] = useState(false);

  // Check for stored access token and validate it
  useEffect(() => {
    const checkStoredToken = async () => {
      let storedToken = localStorage.getItem('access_token');
      let rememberMe = localStorage.getItem('remember_me') === 'true';
      
      // If rememberMe is false but Redux has access token, use Redux token
      if (!rememberMe && accessToken) {
        storedToken = accessToken;
        rememberMe = true;
        // Update localStorage to reflect the rememberMe state
        localStorage.setItem('remember_me', 'true');
        localStorage.setItem('access_token', accessToken);
      }
      
      if (rememberMe && storedToken) {
        setIsCheckingToken(true);
        try {
          const data = await graphqlRequest(GQ_ME_QUERY, {}, storedToken);

          if (data.me) {
            // Token is valid, set auth state and show profile
            dispatch(setAuthState({
              user: data.me,
              accessToken: storedToken,
              refreshToken: localStorage.getItem('refresh_token') || '',
              isAuthenticated: true
            }));
            onSuccess(); // This will show the profile page
          } else {
            // Token is invalid, clear stored data
            localStorage.removeItem('remember_me');
          }
        } catch (error) {
          // Network error or invalid token, clear stored data
          localStorage.removeItem('remember_me');
        } finally {
          setIsCheckingToken(false);
        }
      }
    };

    checkStoredToken();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    dispatch(clearError());

    const credentials = {
      username: formData.username,
      password: formData.password
    };

    try {
      const result = await dispatch(loginUser(credentials));
      if (loginUser.fulfilled.match(result)) {
        // Redux is cleared when page reload. so in AuthInitializer, need to set token in redux store again.
        localStorage.setItem('access_token', result.payload.access_token);
        localStorage.setItem('refresh_token', result.payload.refresh_token);

        // Handle remember me functionality
        if (formData.rememberMe) {
          // Save access token and refresh token to localStorage
          localStorage.setItem('remember_me', 'true');
        } else {
          // Clear remembered tokens if not checked
          localStorage.removeItem('remember_me');
        }
        
        onSuccess();
      }
    } catch (error) {
      // Error is handled by Redux
      console.error('Login error:', error);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      dispatch(clearError());
      
      const variables = {
        input: {
          googleToken: credentialResponse.credential
        }
      };

      const data = await graphqlRequest(GQ_LOGIN_WITH_GOOGLE, variables);
      
      if (data.loginWithGoogle?.token) {
        const { accessToken, refreshToken, user } = data.loginWithGoogle.token;
        
        // Store tokens in localStorage
        localStorage.setItem('access_token', accessToken);
        localStorage.setItem('refresh_token', refreshToken);
        localStorage.setItem('remember_me', 'true');
        
        // Update Redux state
        dispatch(setAuthState({
          user: user,
          accessToken: accessToken,
          refreshToken: refreshToken,
          isAuthenticated: true
        }));
        
        onSuccess();
      }
    } catch (error) {
      console.error('Google login error:', error);
      dispatch(setAuthState({
        error: error.message || 'Google login failed. Please try again.'
      }));
    }
  };

  const handleGoogleError = () => {
    console.error('Google login failed');
    dispatch(setAuthState({
      error: 'Google login failed. Please try again.'
    }));
  };

  return (
    <>
      <div className="modal-header">
        <h2 className="modal-title">Log In to Save Your Build</h2>
      </div>
      <div className="modal-body saveBuildModalBody">
        <form className="loginForm" onSubmit={handleLogin}>
          <div>
            <label htmlFor="username">Email Address:</label> <br />
            <input 
              className="input_100" 
              name="username" 
              type="email" 
              value={formData.username}
              onChange={handleInputChange}
              required 
            />
          </div>
          <div>
            <label htmlFor="password">Password:</label> <br/>
            <input 
              autoComplete="off" 
              className="input_100" 
              name="password" 
              type="password" 
              value={formData.password}
              onChange={handleInputChange}
              required 
            />
          </div>
          <div>
            <label>
              <input 
                data-val="true" 
                name="rememberMe" 
                type="checkbox" 
                checked={formData.rememberMe}
                onChange={handleInputChange}
              /> Remember me?
            </label>
          </div>
          <div>
            <button 
              className="orange-button button saveBuildButton" 
              type="submit"
              disabled={isLoading || isCheckingToken}
            >
              {isLoading ? 'Signing In...' : isCheckingToken ? 'Checking...' : 'Sign In'}
            </button>
          </div>
          {error && <div className="error-message">{error}</div>}
          <div className="passwordLink">
            <a href="#" onClick={onShowForgotPassword} className="link-button">Forgot your password?</a>
          </div>
        </form>
        
        <div className="google-login-section">
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            useOneTap={false}
            theme="outline"
            size="large"
            width="100%"
            text="continue_with"
          />
        </div>
        
        <div className="signupLink">
          <span>Don't have an account? </span><a href="#" onClick={onShowSignup} className="link-button">Sign up</a>
        </div>
      </div>
    </>
  );
}